import React, { useRef, useState } from 'react';
import { Plus, Trash2, List, X, AlertTriangle } from 'lucide-react';
import { cn } from '../../ui';
import SortableList, { DragHandle } from './SortableList';
import { canonicalType } from '../fieldTypeAliases';
import ConfirmDialog from './ConfirmDialog';

/**
 * FieldSchemaEditor
 *
 * Editor del `fields_schema` de un tipo. Replica el sneak peek validado
 * del mockup v3:
 *   - tabla de campos reordenables por drag (asa ⋮)
 *   - cada campo: nombre, tipo (select de tipos), único por unidad,
 *     obligatorio, eliminar
 *   - si el tipo del campo es "select", debajo se despliega el panel de
 *     opciones (reordenables, editables inline, añadir/quitar)
 *
 * Props:
 *   value: array de campos
 *     { name, type, required, per_unit, options?: string[] }
 *   onChange: (nextSchema) => void
 *   bloqueados: nombres de campo que el editor NO deja cambiar ni borrar (por defecto, ninguno).
 *     Son `provincia` y `localidad` cuando el tipo sigue su ubicación: los pone y los quita el
 *     interruptor «Seguir su ubicación» (Fase 3b), y el servidor rechaza otra forma. Se pueden
 *     reordenar y marcar «En listado».
 *
 * Tipos soportados = los que valida el backend (schemaValidator.js).
 */

// Tipos soportados por el backend (VALID_FIELD_TYPES del schemaValidator).
// El editor habla el vocabulario CANÓNICO del backend: usa 'company_link'
// (no el alias 'empresa_link'), porque en BBDD el fields_schema se guarda
// siempre canónico. Emitir el alias mientras el backend guarda el canónico
// rompía el round-trip (el <select> no encontraba su <option> al reabrir y
// caía en la primera → el campo parecía "desaparecer").
// Se han retirado boolean/datetime/email/url: el backend no los soporta
// (elegirlos provocaba un 400 al crear el tipo).
export const FIELD_TYPES = [
  { value: 'string', label: 'string' },
  { value: 'text', label: 'text' },
  { value: 'number', label: 'number' },
  { value: 'decimal', label: 'decimal' },
  { value: 'date', label: 'date' },
  { value: 'select', label: 'select' },
  { value: 'user_link', label: 'Usuario' },
  { value: 'office_link', label: 'Oficina' },
  { value: 'company_link', label: 'Empresa' },
  { value: 'business_link', label: 'Negocio' },
];

// ¿El tipo está entre los que el editor sabe pintar? Se usa aquí (aviso
// visible + <option> "no reconocido") y en TipoForm (bloqueo de guardado).
// Espera un tipo YA canonicalizado (canonicalType).
export function isKnownFieldType(type) {
  return FIELD_TYPES.some((t) => t.value === type);
}

// Nombre de columna FIJO que exige el backend para cada *_link. El
// usuario no debe escribirlo: se asigna solo al elegir el tipo.
const LINK_FIXED_NAME = {
  user_link: 'usuario_id',
  empresa_link: 'empresa_id',
  company_link: 'empresa_id',
  office_link: 'oficina_id',
  business_link: 'negocio_id',
};

// Deriva el nombre técnico (columna BBDD) a partir de la etiqueta:
// quita tildes, pasa a snake_case, recorta a 30 chars. Para campos no
// vinculados; los *_link usan LINK_FIXED_NAME.
function deriveName(label) {
  let s = String(label || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // quita acentos
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/_+/g, '_');
  if (!s) s = 'campo';
  if (/^[0-9]/.test(s)) s = 'f_' + s;
  return s.slice(0, 30);
}

// Calcula el nombre técnico de un campo según su tipo y etiqueta.
function autoName(type, label) {
  if (LINK_FIXED_NAME[type]) return LINK_FIXED_NAME[type];
  return deriveName(label);
}

export default function FieldSchemaEditor({ value, onChange, bloqueados = [] }) {
  // Hidratación: canonicaliza el `type` de cada campo ANTES de pintar, para
  // que un valor guardado como canónico (company_link) —o un alias legacy
  // (empresa_link)— case con su <option> del dropdown. Sin esto, el <select>
  // no encontraba opción y caía en la primera. Se preserva la identidad del
  // objeto cuando el tipo no cambia, para no forzar re-renders inútiles.
  const rawSchema = Array.isArray(value) ? value : [];
  const schema = rawSchema.map((f) => {
    const canon = canonicalType(f && f.type);
    return f && canon === f.type ? f : { ...f, type: canon };
  });

  // Campos cuyo tipo (ya canónico) el editor NO conoce: no deben guardarse
  // con el tipo mutado a la primera opción. Se marca el aviso y el guardado
  // queda bloqueado desde TipoForm (isKnownFieldType).
  const hayTipoDesconocido = schema.some((f) => !isKnownFieldType(f.type));

  // Nombres que existían al montar el editor = campos ya creados en
  // BBDD. Su nombre técnico es INMUTABLE (no se regenera aunque cambie
  // la etiqueta). Los campos nuevos sí regeneran el nombre solos.
  // Campo existente pendiente de confirmar su borrado (índice), o null.
  const [porQuitar, setPorQuitar] = useState(null);
  const campoPorQuitar = porQuitar !== null ? schema[porQuitar] : null;
  const etiquetaPorQuitar = (campoPorQuitar && (campoPorQuitar.label || campoPorQuitar.name)) || 'sin etiqueta';

  const initialNames = useRef(null);
  if (initialNames.current === null) {
    initialNames.current = new Set(
      schema.filter((f) => f && f.name).map((f) => f.name)
    );
  }
  function esExistente(field) {
    return !!(field && field.name) && initialNames.current.has(field.name);
  }

  function update(nextSchema) {
    onChange(nextSchema);
  }

  function patchField(index, patch) {
    const next = schema.map((f, i) => (i === index ? { ...f, ...patch } : f));
    update(next);
  }

  // Cambiar la etiqueta: para campos NUEVOS no vinculados, regenera el
  // nombre técnico automáticamente. Para campos existentes o *_link, el
  // nombre no se toca.
  function changeLabel(index, label) {
    const field = schema[index];
    const patch = { label };
    if (!esExistente(field) && !LINK_FIXED_NAME[field.type]) {
      patch.name = deriveName(label);
    }
    patchField(index, patch);
  }

  function addField() {
    update([
      ...schema,
      { name: '', label: '', type: 'string', required: false, per_unit: false, show_in_list: true, miles: false },
    ]);
  }

  function removeField(index) {
    const field = schema[index];
    // Campo nuevo (aún no persistido): quitar sin más.
    // Campo existente (ya tiene columna en BBDD): pedir confirmación
    // explícita; el DROP COLUMN real ocurre al pulsar Guardar.
    if (esExistente(field)) {
      setPorQuitar(index); // lo confirma el diálogo de abajo
      return;
    }
    update(schema.filter((_, i) => i !== index));
  }

  function changeType(index, type) {
    const field = schema[index];
    const patch = { type };
    if (type === 'select' && !Array.isArray(field.options)) {
      patch.options = [''];
    }
    if (type !== 'select') {
      patch.options = undefined;
    }
    // miles solo aplica a number/decimal — al salir de esos tipos lo
    // limpiamos para no dejar metadata huérfana en el schema.
    if (type !== 'number' && type !== 'decimal') {
      patch.miles = undefined;
    }
    // Recalcular el nombre técnico salvo en campos ya existentes
    // (cuyo nombre es inmutable). Para *_link → nombre fijo; para el
    // resto → derivado de la etiqueta actual.
    if (!esExistente(field)) {
      patch.name = autoName(type, field.label);
    }
    patchField(index, patch);
  }

  // ── Opciones de un campo select ──
  function setOptions(index, options) {
    patchField(index, { options });
  }
  function addOption(index) {
    const opts = schema[index].options || [];
    setOptions(index, [...opts, '']);
  }
  function removeOption(fieldIndex, optIndex) {
    const opts = schema[fieldIndex].options || [];
    setOptions(fieldIndex, opts.filter((_, i) => i !== optIndex));
  }
  function changeOption(fieldIndex, optIndex, val) {
    const opts = [...(schema[fieldIndex].options || [])];
    opts[optIndex] = val;
    setOptions(fieldIndex, opts);
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
          Esquema de campos del tipo
        </p>
        <span className="text-[10px] text-slate-400">
          Arrastra desde el icono ⋮ para reordenar
        </span>
      </div>

      {hayTipoDesconocido && (
        <div className="mb-2 bg-amber-50 border border-amber-300 rounded-md p-3 text-xs text-amber-800 flex items-start gap-2">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <div>
            Hay uno o más campos con un <span className="font-medium">tipo no
            reconocido</span> (marcados en ámbar). El editor no reescribe un
            tipo que no entiende: elige un tipo válido en cada uno para poder
            guardar. No se guardará el tipo mutado.
          </div>
        </div>
      )}

      <div className="border border-slate-200 rounded-md overflow-hidden">
        {/* Cabecera */}
        <div className="bg-slate-50 text-slate-500 text-xs flex items-center px-2 py-2">
          <span className="w-8" />
          <span className="flex-1 px-2">Etiqueta</span>
          <span className="w-32 px-2">Tipo</span>
          <span className="w-20 px-2 text-center">Único/ud.</span>
          <span className="w-20 px-2 text-center">Obligatorio</span>
          <span className="w-20 px-2 text-center">En listado</span>
          <span className="w-8" />
        </div>

        {schema.length === 0 ? (
          <div className="px-4 py-6 text-center text-xs text-slate-400">
            Sin campos todavía. Añade el primero abajo.
          </div>
        ) : (
          <SortableList
            items={schema}
            getKey={(_, i) => i}
            onReorder={update}
            className="divide-y divide-slate-100"
            renderItem={(field, index, dragHandleProps) => {
              const isSelect = field.type === 'select';
              const esNumerico = field.type === 'number' || field.type === 'decimal';
              const tipoDesconocido = !isKnownFieldType(field.type);
              const bloqueado = bloqueados.includes(field.name);
              return (
                <div className={cn(isSelect && 'bg-blue-50/40')}>
                  {/* Fila del campo */}
                  <div className="flex items-center px-2 py-2 text-xs">
                    <span className="w-8 flex justify-center">
                      <DragHandle dragHandleProps={dragHandleProps} />
                    </span>

                    <span className="flex-1 px-2">
                      <input
                        value={field.label || ''}
                        onChange={(e) => changeLabel(index, e.target.value)}
                        placeholder="p. ej. Usuario asignado"
                        disabled={bloqueado}
                        title={bloqueado ? 'Campo de ubicación: lo pone el interruptor «Seguir su ubicación»' : undefined}
                        className={cn(
                          'w-full border border-slate-200 rounded px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-blue-200',
                          bloqueado && 'bg-slate-50 text-slate-500 cursor-not-allowed'
                        )}
                      />
                    </span>

                    <span className="w-32 px-2">
                      <select
                        value={field.type}
                        onChange={(e) => changeType(index, e.target.value)}
                        disabled={bloqueado}
                        className={cn(
                          'w-full border rounded px-2 py-1 text-xs bg-white focus:outline-none focus:ring-2',
                          tipoDesconocido
                            ? 'border-amber-400 ring-amber-200 focus:ring-amber-200'
                            : 'border-slate-200 focus:ring-blue-200'
                        )}
                      >
                        {/* Tipo que el editor no reconoce: se preserva su
                            valor original (no se cae en la primera opción) y
                            se marca deshabilitado para forzar al usuario a
                            elegir uno válido antes de poder guardar. */}
                        {tipoDesconocido && (
                          <option value={field.type} disabled>
                            Tipo no reconocido: {field.type}
                          </option>
                        )}
                        {FIELD_TYPES.map((t) => (
                          <option key={t.value} value={t.value}>
                            {t.label}
                          </option>
                        ))}
                      </select>
                    </span>

                    <span className="w-20 px-2 flex justify-center">
                      <input
                        type="checkbox"
                        checked={!!field.per_unit}
                        onChange={(e) => patchField(index, { per_unit: e.target.checked })}
                        disabled={bloqueado}
                        className="rounded"
                      />
                    </span>

                    <span className="w-20 px-2 flex justify-center">
                      <input
                        type="checkbox"
                        checked={!!field.required}
                        onChange={(e) => patchField(index, { required: e.target.checked })}
                        disabled={bloqueado}
                        className="rounded"
                      />
                    </span>

                    <span className="w-20 px-2 flex justify-center">
                      <input
                        type="checkbox"
                        checked={!!field.show_in_list}
                        onChange={(e) => patchField(index, { show_in_list: e.target.checked })}
                        className="rounded"
                        title="Mostrar este campo como columna en el listado de activos"
                      />
                    </span>

                    <span className="w-8 flex justify-center">
                      {!bloqueado && <button
                        type="button"
                        onClick={() => removeField(index)}
                        className="text-slate-400 hover:text-rose-600"
                        aria-label="Eliminar campo"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>}
                    </span>
                  </div>

                  {/* Opción "Separador de miles" — solo para number/decimal.
                      Sin separador para años, 4 últimos dígitos VISA, etc.
                      Con separador para kilometraje, importes, etc. */}
                  {esNumerico && (
                    <div className="pl-10 pr-3 pb-2 -mt-1">
                      <label className="inline-flex items-center gap-2 text-[11px] text-slate-600 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={!!field.miles}
                          onChange={(e) => patchField(index, { miles: e.target.checked })}
                          className="rounded"
                        />
                        <span>
                          Separador de miles{' '}
                          <span className="text-slate-400">
                            (p. ej. 20.000 en lugar de 20000)
                          </span>
                        </span>
                      </label>
                    </div>
                  )}

                  {bloqueado && (
                    <div className="pl-10 pr-3 pb-2 -mt-1 text-[11px] text-slate-500">
                      {field.name === 'provincia'
                        ? 'Las 52 provincias del INE. Lo pone el interruptor «Seguir su ubicación».'
                        : 'Municipio del catálogo del INE de la provincia elegida. Lo pone el interruptor «Seguir su ubicación».'}
                    </div>
                  )}

                  {/* Panel de opciones si type=select */}
                  {isSelect && !bloqueado && (
                    <div className="px-2 pb-3 pl-10 pr-3">
                      <div className="border border-blue-200 rounded-md bg-white">
                        <div className="px-3 py-2 border-b border-blue-100 flex items-center justify-between">
                          <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
                            <List className="w-3 h-3" />
                            Opciones del select · {field.name || 'campo'}
                          </span>
                          <span className="text-[10px] text-slate-400">
                            Arrastra para reordenar
                          </span>
                        </div>

                        {(field.options || []).length === 0 ? (
                          <div className="px-3 py-2 text-[11px] text-slate-400">
                            Sin opciones. Añade al menos una.
                          </div>
                        ) : (
                          <SortableList
                            items={field.options || []}
                            getKey={(_, i) => i}
                            onReorder={(opts) => setOptions(index, opts)}
                            className="divide-y divide-slate-100"
                            renderItem={(opt, optIndex, optDragProps) => (
                              <div className="flex items-center gap-2 px-3 py-1.5">
                                <DragHandle
                                  dragHandleProps={optDragProps}
                                  size="w-3.5 h-3.5"
                                />
                                <input
                                  value={opt}
                                  onChange={(e) =>
                                    changeOption(index, optIndex, e.target.value)
                                  }
                                  placeholder="Valor de la opción"
                                  className="flex-1 border border-slate-200 rounded px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-blue-200"
                                />
                                <button
                                  type="button"
                                  onClick={() => removeOption(index, optIndex)}
                                  className="text-slate-400 hover:text-rose-600 p-1"
                                  aria-label="Eliminar opción"
                                >
                                  <X className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            )}
                          />
                        )}

                        <button
                          type="button"
                          onClick={() => addOption(index)}
                          className="w-full text-left px-3 py-1.5 text-xs text-[hsl(var(--brand-light))] hover:bg-blue-50/60 border-t border-blue-100 flex items-center gap-2"
                        >
                          <Plus className="w-3.5 h-3.5" />
                          Añadir opción
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            }}
          />
        )}

        {/* Añadir campo */}
        <button
          type="button"
          onClick={addField}
          className="w-full text-left px-3 py-2 text-xs text-[hsl(var(--brand-light))] hover:bg-slate-50 border-t border-slate-100 flex items-center gap-2"
        >
          <Plus className="w-3.5 h-3.5" />
          Añadir campo
        </button>
      </div>

      <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-xs text-slate-600 flex items-start gap-2 mt-3">
        <List className="w-3.5 h-3.5 mt-0.5 shrink-0" />
        <div>
          <span className="font-medium">Etiqueta</span> es lo que ven los
          usuarios en formularios y tablas (p. ej. "Usuario asignado"). El
          nombre de columna en la base de datos se genera automáticamente
          a partir de la etiqueta y no necesitas gestionarlo.
          <br />
          El tipo <span className="font-mono">select</span> despliega el
          panel de opciones de arriba. Los tipos de vínculo (Usuario,
          Empresa, Oficina, Negocio) enlazan automáticamente con su tabla
          correspondiente.
          <br />
          <span className="font-medium">En listado</span> controla qué
          campos aparecen como columna en la tabla de activos. Marca solo
          los relevantes (p. ej. Modelo, Matrícula, Fecha ITV) y deja sin
          marcar los secundarios (p. ej. Color): seguirán visibles en el
          detalle del activo. Si no marcas ninguno, se muestran los 4
          primeros campos por defecto.
        </div>
      </div>

      <ConfirmDialog
        open={porQuitar !== null}
        onClose={() => setPorQuitar(null)}
        titulo={`Eliminar el campo «${etiquetaPorQuitar}»`}
        textoConfirmar="Eliminar el campo"
        destructivo
        onConfirm={() => {
          update(schema.filter((_, i) => i !== porQuitar));
          setPorQuitar(null);
        }}
      >
        <p>
          Al guardar el tipo se borrarán <span className="font-medium">todos los datos</span> de
          esa columna, de forma irreversible.
        </p>
      </ConfirmDialog>
    </div>
  );
}
